import axios from 'axios'
import { customAlphabet } from 'nanoid'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { queryClient } from '../../../index'
import { getRedirectUrl } from '../../../pageTpls/ContributeTpl/utils'
import { UIContent } from '../../../UIContent'
import { isDeviceOnline } from '../../environmentConfig'


import { useGetQueryData } from '../general'
import { useAppSettingsContext } from '../../../contexts/appSettingsContext'
import { useBannerContext } from 'oolib'
import { useGenErrorMsgBanners } from '../../useGenErrorMsgBanners'



const getContribution = async ({
  contentType,
  contentId,
  mode,
  activeLang,
  firstLoad = false,
}) => {
  try {
    const url = mode === "view"
      ? `/api/content/viewContent/${contentType}/${contentId}?firstLoad=${firstLoad?.current}`
      : `/api/content/getContentForEdit/${contentType}/${contentId}`;
    


    let res;
    if (mode === "view") {
      res = await axios.get(url, { params: { activeLang } });
    } else {
      res = await axios.patch(url, { activeLang });
    }

    return { ...res.data };
  } catch (error) {
    throw error;
  }
};


export const useGetContribution = ({
  contentType,
  contentId,
  mode,
  enabled,
  firstLoad = false,
}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = mode==="view"? APP_SETTINGS.lang?.value:undefined
  return useQuery(
    ["Contributions", mode, contentType, contentId, activeLang].filter((d)=>d!==undefined), // important
    () =>
      getContribution({
        contentType,
        contentId,
        mode,
        activeLang,
        firstLoad,
      }),
    {
      enabled:
        enabled !== undefined
          ? enabled
          : (mode === "edit" || mode === "view")
          ? true
          : false,
    }
  );
};

export const useAutoSave = () => {
	const { SET_ALERT_BANNER } = useBannerContext()
  

  return useMutation(
    ({ updateFields, contentId, contentType, ACTION }) => {
			
			const config = { headers: { 'Content-Type': 'application/json' } }

      return axios.post(
				`/api/contributions${ACTION ? `?userAction=${ACTION}` : ''}`,
        { updateFields, contentId, contentType },
				config,
			)
    },
    {
      onSuccess: (res) => {
				if (res.data.updatedContent.kp_published_status === 'underModeration') {
          queryClient.setQueryData(
            [
							'ContributionCloneForModeration',
							'moderate',
              res.data.updatedContent.meta.kp_content_type,
              res.data.updatedContent.kp_pre_mod_doc,
            ],
						(prev) => ({ ...prev, contribution: res.data.updatedContent }),
					)
         

				} else if (res.data.updatedContent.kp_published_status === 'editPublished') {
          queryClient.setQueryData(
            [
							'Contributions',
							'edit',
              res.data.updatedContent.meta.kp_content_type,
              res.data.updatedContent.kp_og_published_doc,
            ],
						(prev) => ({ ...prev, contribution: res.data.updatedContent }),
					)
				} else if (res.data.updatedContent.kp_published_status === 'draft') {
          queryClient.setQueryData(
            [
							'Contributions',
							'edit',
              res.data.updatedContent.meta.kp_content_type,
              res.data.updatedContent._id,
            ],
						(prev) => ({ ...prev, contribution: res.data.updatedContent }),
					)
        }

      },
      onError: () =>
        SET_ALERT_BANNER({
					color: 'red',
					msg: UIContent.reactQueryHooks_contributions.autosaveErrorMsg,
					timeOut: 3000,
				}),
		},
	)
    }


export const useContributionCloneForModeration = ({
  contentType,
  contentId,
  mode,
}) => {
  return useQuery(
		['ContributionCloneForModeration', mode, contentType, contentId],
    () => {
      return axios
        .get(`/api/moderation/edit/${contentType}?id=${contentId}`)
				.then((res) => res.data)
    },
		{ enabled: navigator.onLine && mode === 'moderate' ? true : false },
	)
}

export const useModerationApprove = ()=>{
  const queryClient = useQueryClient();
  const {SET_ALERT_BANNER} = useBannerContext();
  const navigate = useNavigate();
  const {SELF_MANAGED_BASE_CONFIGS} = useGetQueryData('platformConfigs')
  return useMutation(
    ({modCloneId,contentType})=>{
      return axios.post(
        `/api/moderation2/approveByModerator/${contentType}/${modCloneId}`,
        { headers: { "Content-Type": "application/json" }}
      );
    },
    {

      onSuccess: (res) => {

        // remove the query instead of invalidate query because the content of the lexical editor wont update on a re-render.
        queryClient.removeQueries(
          [ "Contributions", 'view', res.data.publishedDoc.meta.kp_content_type, res.data.publishedDoc._id ]
        );
        navigate(getRedirectUrl.onPublish({ content: res.data.publishedDoc, SELF_MANAGED_BASE_CONFIGS })); 
        //read note on lie 192-3 explaining why removeQueries
        queryClient.removeQueries(
          ['ContributionCloneForModeration', 
          'moderate', 
            res.data.publishedDoc.meta.kp_content_type,
          res.data.publishedDoc._id
        ], {exact: true})

        SET_ALERT_BANNER({
          color : 'green',
          msg : UIContent.reactQueryHooks_contributions.modApproveSuccessfulMsg,
          timeOut : 4000
        })
        queryClient.invalidateQueries("modQueue");
       
      },
      onError: () =>{
        SET_ALERT_BANNER({
          color:"red",
          msg: UIContent.reactQueryHooks_contributions.modApproveErrorMsg,
          timeOut:4000
        })
      }
      },
    
  )
    }


export const useGetModQueue = () => useQuery(
  "modQueue", 
  () => axios.get('/api/moderation/view').then(res => res.data)
  );

export const useCreateAndPublishContent = () => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({ contentType, content = {} }) =>
      axios.post(
        `/api/content/createAndPublishContent/${contentType}`,
        content,
        { headers: { "Content-Type": "application/json" } }
      ),
    {
      onSuccess: (res) => {
        const { kp_content_type } = res.data.content.meta;
        queryClient.invalidateQueries(["contentListing", "lazy", [kp_content_type]]);
      },
      onError: (err) => genErrorMsgBanners({ err }),
    }
  );
};
